import { BaseAction, State, selectPartiposter } from './index'
import _ from 'lodash'
import {
    DeleteTagRequestPayload,
    GetPartiposterTagListRequestPayload,
    SavePartiposterTagRequestPayload,
    UpdatePartiposterTagRequestPayload,
} from 'services/api/partiposter'
import { PartiposterProfile } from './submissions'
import { Tag } from 'services/api/constants'
import { TagCategoryEnum } from './constants'

export enum PartiposterTagType {
    HISTORY = 'HISTORY',
    ADD = 'ADD',
}

interface UserInfo {
    uuid: string
    name: string
}

export interface TagDetail {
    campaign_title: string
    brand_name: string
    text?: string
    updated_at: string
    last_updater_user_info: UserInfo
    creator_user_info: UserInfo
    uuid: string // profile to tag uuid
}

export interface ProfileToTagGrouped {
    uuid: string // tag uuid
    campaign_count: number
    latest_tagged_detail: TagDetail
    count?: number
}

export interface ProfileToTagGroupDetailed extends ProfileToTagGrouped, Tag {}

export interface PartiposterTag extends TagDetail {
    tag_uuid: string
    submission_uuid?: string
    created_at: string
    tag_enum?: string // TODO missing?
}

export interface TagGroup {
    tag: Tag
    partiposterTags: PartiposterTag[]
}

export interface ProfileToTagGroupedById {
    [uuid: string]: TagGroup
}

interface ClearPartiposterTagListPayload {
    type: PartiposterTagType
}

interface SavePartiposterTagListPayload extends ClearPartiposterTagListPayload {
    data: PartiposterTag[]
}

interface BaseModalState {
    isOpen: boolean
    selectedProfile: PartiposterProfile
    submissionUuid?: string
}

export interface TagHistoryModalState extends BaseModalState {
    campaignUuid?: string
}

interface PartiposterTagsEntityState {
    [uuid: string]: PartiposterTag
}
interface PartiposterTagTypesEntityState {
    partiposterTags: string[]
    byId: PartiposterTagsEntityState
}

interface PartiposterTagTypeEntityState {
    [PartiposterTagType.ADD]: PartiposterTagTypesEntityState
    [PartiposterTagType.HISTORY]: PartiposterTagTypesEntityState
}

interface DeleteTagModalState extends BaseModalState {
    selectedTag: Tag | undefined
    uuid: string
    nextPartiposterTag?: PartiposterTag
}

interface ProfileTagModalState extends Omit<DeleteTagModalState, 'uuid'> {
    uuid: string | null
    categoryEnum: TagCategoryEnum | undefined
}

export interface SavePartiposterTagPayload {
    submissionUuid?: string
    profileUuid: string
    tags: PartiposterTag[]
}

interface TagModalState extends TagHistoryModalState {
    errors?: any[]
}

export interface PartiposterState {
    partiposterTagType: PartiposterTagTypeEntityState
    tagHistoryModal: TagHistoryModalState
    tagModal: TagModalState
    profileTagModal: ProfileTagModalState
    deleteTagModal: DeleteTagModalState
    byId: Record<string, PartiposterProfile>
}

const initialState: PartiposterState = {
    partiposterTagType: {} as PartiposterTagTypeEntityState,
    tagHistoryModal: {} as TagHistoryModalState,
    tagModal: {} as TagModalState,
    profileTagModal: {} as ProfileTagModalState,
    deleteTagModal: {} as DeleteTagModalState,
    byId: {},
}

export enum PartiposterActions {
    GET_PARTIPOSTER_TAG_LIST_REQUEST = 'partiposter/GET_PARTIPOSTER_TAG_LIST_REQUEST',
    SAVE_PARTIPOSTER_TAG_LIST = 'partiposter/SAVE_PARTIPOSTER_TAG_LIST',
    CLEAR_PARTIPOSTER_TAG_LIST = 'partiposter/CLEAR_PARTIPOSTER_TAG_LIST',
    SAVE_PARTIPOSTER_TAG_REQUEST = 'partiposter/SAVE_PARTIPOSTER_TAG_REQUEST',
    SAVE_PARTIPOSTER_TAG = 'partiposter/SAVE_PARTIPOSTER_TAG',
    UPDATE_PARTIPOSTER_TAG_REQUEST = 'partiposter/UPDATE_PARTIPOSTER_TAG_REQUEST',
    UPDATE_PARTIPOSTER_TAG = 'partiposter/UPDATE_PARTIPOSTER_TAG',
    DELETE_PARTIPOSTER_TAG_REQUEST = 'partiposter/DELETE_PARTIPOSTER_TAG_REQUEST',
    DELETE_PARTIPOSTER_TAG = 'partiposter/DELETE_PARTIPOSTER_TAG',
    OPEN_TAG_HISTORY_MODAL = 'partiposter/OPEN_TAG_HISTORY_MODAL',
    CLOSE_TAG_HISTORY_MODAL = 'partiposter/CLOSE_TAG_HISTORY_MODAL',
    OPEN_TAG_MODAL = 'partiposter/OPEN_TAG_MODAL',
    CLOSE_TAG_MODAL = 'partiposter/CLOSE_TAG_MODAL',
    SET_TAG_MODAL_ERRORS = 'partiposter/SET_TAG_MODAL_ERRORS',
    CLEAR_TAG_MODAL_ERRORS = 'partiposter/CLEAR_TAG_MODAL_ERRORS',
    OPEN_PROFILE_TAG_MODAL = 'partiposter/OPEN_PROFILE_TAG_MODAL',
    CLOSE_PROFILE_TAG_MODAL = 'partiposter/CLOSE_PROFILE_TAG_MODAL',
    OPEN_DELETE_TAG_MODAL = 'partiposter/OPEN_DELETE_TAG_MODAL',
    CLOSE_DELETE_TAG_MODAL = 'partiposter/CLOSE_DELETE_TAG_MODAL',
    SAVE_PARTIPOSTER_PROFILES = 'partiposter/SAVE_PARTIPOSTER_PROFILES',
}

export const openDeleteTagModal = (payload: DeleteTagModalState) => ({
    payload,
    type: PartiposterActions.OPEN_DELETE_TAG_MODAL,
})

export const closeDeleteTagModal = () => ({
    type: PartiposterActions.CLOSE_DELETE_TAG_MODAL,
})

export const openProfileTagModal = (payload: ProfileTagModalState) => ({
    payload,
    type: PartiposterActions.OPEN_PROFILE_TAG_MODAL,
})

export const closeProfileTagModal = () => ({
    type: PartiposterActions.CLOSE_PROFILE_TAG_MODAL,
})

export const openTagModal = (payload: TagHistoryModalState) => ({
    payload,
    type: PartiposterActions.OPEN_TAG_MODAL,
})

export const setTagModalErrors = (payload: any[]) => ({
    payload,
    type: PartiposterActions.SET_TAG_MODAL_ERRORS,
})

export const clearTagModalErrors = () => ({
    type: PartiposterActions.CLEAR_TAG_MODAL_ERRORS,
})

export const closeTagModal = () => ({
    type: PartiposterActions.CLOSE_TAG_MODAL,
})

export const openTagHistoryModal = (payload: TagHistoryModalState) => ({
    payload,
    type: PartiposterActions.OPEN_TAG_HISTORY_MODAL,
})

export const closeTagHistoryModal = () => ({
    type: PartiposterActions.CLOSE_TAG_HISTORY_MODAL,
})

export const getPartiposterTagListRequest = (payload: GetPartiposterTagListRequestPayload) => ({
    payload,
    type: PartiposterActions.GET_PARTIPOSTER_TAG_LIST_REQUEST,
})

export const savePartiposterTagList = (payload: SavePartiposterTagListPayload) => ({
    payload,
    type: PartiposterActions.SAVE_PARTIPOSTER_TAG_LIST,
})

export const clearPartiposterTagList = (payload: ClearPartiposterTagListPayload) => ({
    payload,
    type: PartiposterActions.CLEAR_PARTIPOSTER_TAG_LIST,
})

export const savePartiposterTagRequest = (payload: SavePartiposterTagRequestPayload) => ({
    payload,
    type: PartiposterActions.SAVE_PARTIPOSTER_TAG_REQUEST,
})

export const savePartiposterTag = (payload: SavePartiposterTagPayload) => ({
    payload,
    type: PartiposterActions.SAVE_PARTIPOSTER_TAG,
})

export const updatePartiposterTagRequest = (payload: UpdatePartiposterTagRequestPayload) => ({
    payload,
    type: PartiposterActions.UPDATE_PARTIPOSTER_TAG_REQUEST,
})

export const updatePartiposterTag = (payload: UpdatePartiposterTagRequestPayload) => ({
    payload,
    type: PartiposterActions.UPDATE_PARTIPOSTER_TAG,
})

export const deletePartiposterTagRequest = (payload: DeleteTagRequestPayload) => ({
    payload,
    type: PartiposterActions.DELETE_PARTIPOSTER_TAG_REQUEST,
})

export const deletePartiposterTag = (payload: DeleteTagRequestPayload) => ({
    payload,
    type: PartiposterActions.DELETE_PARTIPOSTER_TAG,
})

export const savePartiposterProfiles = (payload: Record<string, PartiposterProfile>) => ({
    payload,
    type: PartiposterActions.SAVE_PARTIPOSTER_PROFILES,
})

export const partiposterReducer = (state: PartiposterState = initialState, action: BaseAction): PartiposterState => {
    switch (action.type) {
        case PartiposterActions.SAVE_PARTIPOSTER_TAG_LIST: {
            const { data, type }: SavePartiposterTagListPayload = action.payload
            const uuids = data.map((profileTag: PartiposterTag) => profileTag.uuid)

            let partiposterTags: string[] = []
            let byId: PartiposterTagsEntityState = {}
            if (state.partiposterTagType[type]) {
                partiposterTags = state.partiposterTagType[type].partiposterTags
                byId = state.partiposterTagType[type].byId
            }

            return Object.assign({}, state, {
                ...state,
                partiposterTagType: {
                    ...state.partiposterTagType,
                    [type]: {
                        partiposterTags: _.uniq([...partiposterTags, ...uuids]),
                        byId: data.reduce(
                            (memo: PartiposterTagsEntityState, profileTag: PartiposterTag) =>
                                Object.assign({}, memo, { [profileTag.uuid]: profileTag }),
                            byId,
                        ),
                    },
                },
            })
        }
        case PartiposterActions.SAVE_PARTIPOSTER_TAG: {
            const { profileUuid, tags }: SavePartiposterTagPayload = action.payload
            if (!state.byId[profileUuid]) {
                return state
            }
            const updatedTags = [...state.byId[profileUuid].tags]
            tags.forEach(t => {
                const selectedTagIndex = state.byId[profileUuid].tags.findIndex(
                    stateTag => stateTag.uuid === t.tag_uuid,
                )
                const updatedTag: ProfileToTagGrouped = {
                    uuid: t.tag_uuid,
                    campaign_count: 1,
                    latest_tagged_detail: {
                        campaign_title: t.campaign_title,
                        brand_name: t.brand_name,
                        text: t.text,
                        updated_at: t.updated_at,
                        last_updater_user_info: t.last_updater_user_info,
                        creator_user_info: t.creator_user_info,
                        uuid: t.uuid,
                    },
                }
                if (selectedTagIndex === -1) {
                    updatedTags.push({ ...updatedTag })
                } else {
                    updatedTags.splice(selectedTagIndex, 1, {
                        ...updatedTag,
                        campaign_count: updatedTags[selectedTagIndex].campaign_count + 1,
                    })
                }
            })

            const tagUuids = tags.map((profileTag: PartiposterTag) => profileTag.uuid)
            const type: PartiposterTagType = PartiposterTagType.HISTORY
            let newPartiposterTagType
            if (!state.partiposterTagType[type]) {
                newPartiposterTagType = {}
            } else {
                newPartiposterTagType = {
                    [type]: {
                        partiposterTags: _.uniq([...tagUuids, ...state.partiposterTagType[type].partiposterTags]),
                        byId: tags.reduce(
                            (memo: PartiposterTagsEntityState, profileTag: PartiposterTag) =>
                                Object.assign({}, memo, { [profileTag.uuid]: profileTag }),
                            state.partiposterTagType[type].byId,
                        ),
                    },
                }
            }

            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    [profileUuid]: {
                        ...state.byId[profileUuid],
                        tags: updatedTags,
                    },
                },
                partiposterTagType: {
                    ...state.partiposterTagType,
                    ...newPartiposterTagType,
                },
            })
        }
        case PartiposterActions.UPDATE_PARTIPOSTER_TAG: {
            const { uuid, profileUuid, tag_uuid, text }: UpdatePartiposterTagRequestPayload = action.payload

            if (!state.byId[profileUuid]) {
                return state
            }
            const selectedTagIndex = state.byId[profileUuid].tags.findIndex(
                stateTag => stateTag.latest_tagged_detail.uuid === uuid,
            )
            if (selectedTagIndex === -1) {
                return state
            }

            const tags: ProfileToTagGrouped[] = _.cloneDeep(state.byId[profileUuid].tags)
            tags[selectedTagIndex] = {
                ...tags[selectedTagIndex],
                uuid: tag_uuid,
                latest_tagged_detail: {
                    ...tags[selectedTagIndex].latest_tagged_detail,
                    text,
                },
            }

            const type: PartiposterTagType = PartiposterTagType.HISTORY
            let newPartiposterTagType
            if (!state.partiposterTagType[type]) {
                newPartiposterTagType = {}
            } else {
                newPartiposterTagType = {
                    [type]: {
                        ...state.partiposterTagType[type],
                        byId: {
                            ...state.partiposterTagType[type].byId,
                            [uuid]: {
                                ...state.partiposterTagType[type].byId[uuid],
                                text,
                            },
                        },
                    },
                }
            }

            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    [profileUuid]: {
                        ...state.byId[profileUuid],
                        tags,
                    },
                },
                partiposterTagType: {
                    ...state.partiposterTagType,
                    ...newPartiposterTagType,
                },
            })
        }

        case PartiposterActions.DELETE_PARTIPOSTER_TAG: {
            const { uuid, profileUuid, nextPartiposterTag }: DeleteTagRequestPayload = action.payload

            if (!state.byId[profileUuid]) {
                return state
            }

            const type: PartiposterTagType = PartiposterTagType.HISTORY
            let newPartiposterTagType
            if (!state.partiposterTagType[type]) {
                newPartiposterTagType = {}
            } else {
                newPartiposterTagType = {
                    [type]: {
                        ...state.partiposterTagType[type],
                        partiposterTags: _.without(state.partiposterTagType[type].partiposterTags, uuid),
                    },
                }
            }

            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    [profileUuid]: {
                        ...state.byId[profileUuid],
                        tags:
                            nextPartiposterTag === undefined
                                ? state.byId[profileUuid].tags.filter(t =>
                                      t.latest_tagged_detail ? t.latest_tagged_detail.uuid !== uuid : true,
                                  )
                                : state.byId[profileUuid].tags.map(t => {
                                      if (t.uuid === nextPartiposterTag.tag_uuid) {
                                          return {
                                              uuid: nextPartiposterTag.tag_uuid,
                                              campaign_count: t.campaign_count - 1,
                                              latest_tagged_detail: {
                                                  campaign_title: nextPartiposterTag.campaign_title,
                                                  brand_name: nextPartiposterTag.brand_name,
                                                  text: nextPartiposterTag.text,
                                                  updated_at: nextPartiposterTag.updated_at,
                                                  last_updater_user_info: nextPartiposterTag.last_updater_user_info,
                                                  creator_user_info: nextPartiposterTag.creator_user_info,
                                                  uuid: nextPartiposterTag.uuid,
                                              },
                                          }
                                      }
                                      return t
                                  }),
                    },
                },
                partiposterTagType: {
                    ...state.partiposterTagType,
                    ...newPartiposterTagType,
                },
            })
        }
        case PartiposterActions.OPEN_TAG_HISTORY_MODAL: {
            return Object.assign({}, state, {
                tagHistoryModal: action.payload,
            })
        }
        case PartiposterActions.CLOSE_TAG_HISTORY_MODAL: {
            return Object.assign({}, state, {
                tagHistoryModal: initialState.tagHistoryModal,
            })
        }
        case PartiposterActions.OPEN_TAG_MODAL: {
            return Object.assign({}, state, {
                tagModal: action.payload,
            })
        }
        case PartiposterActions.SET_TAG_MODAL_ERRORS: {
            const errors: any[] = action.payload
            return Object.assign({}, state, {
                tagModal: {
                    ...state.tagModal,
                    errors,
                },
            })
        }
        case PartiposterActions.CLEAR_TAG_MODAL_ERRORS: {
            return Object.assign({}, state, {
                tagModal: {
                    ...state.tagModal,
                    errors: [],
                },
            })
        }
        case PartiposterActions.CLOSE_TAG_MODAL: {
            return Object.assign({}, state, {
                tagModal: initialState.tagModal,
            })
        }
        case PartiposterActions.OPEN_PROFILE_TAG_MODAL: {
            return Object.assign({}, state, {
                profileTagModal: action.payload,
            })
        }
        case PartiposterActions.CLOSE_PROFILE_TAG_MODAL: {
            return Object.assign({}, state, {
                profileTagModal: initialState.profileTagModal,
            })
        }
        case PartiposterActions.OPEN_DELETE_TAG_MODAL: {
            return Object.assign({}, state, {
                deleteTagModal: action.payload,
            })
        }
        case PartiposterActions.CLOSE_DELETE_TAG_MODAL: {
            return Object.assign({}, state, {
                deleteTagModal: initialState.deleteTagModal,
            })
        }
        case PartiposterActions.CLEAR_PARTIPOSTER_TAG_LIST: {
            const { type }: ClearPartiposterTagListPayload = action.payload
            return Object.assign({}, state, {
                partiposterTagType: _.omit(state.partiposterTagType, [type]),
            })
        }
        case PartiposterActions.SAVE_PARTIPOSTER_PROFILES: {
            return Object.assign({}, state, {
                byId: {
                    ...state.byId,
                    ...action.payload,
                },
            })
        }
        default:
            return state
    }
}

export const selectTagHistoryModal = (state: State): TagHistoryModalState => selectPartiposter(state).tagHistoryModal
export const selectTagHistoryModalIsOpen = (state: State): boolean => selectPartiposter(state).tagHistoryModal.isOpen
export const selectTagModal = (state: State): TagModalState => selectPartiposter(state).tagModal
export const selectTagModalIsOpen = (state: State): boolean => selectPartiposter(state).tagModal.isOpen
export const selectProfileTagModal = (state: State): ProfileTagModalState => selectPartiposter(state).profileTagModal
export const selectProfileTagModalIsOpen = (state: State): boolean => selectPartiposter(state).profileTagModal.isOpen
export const selectDeleteTagModal = (state: State): DeleteTagModalState => selectPartiposter(state).deleteTagModal
export const selectDeleteTagModalIsOpen = (state: State): boolean => selectPartiposter(state).deleteTagModal.isOpen
export const selectPartiposterTags =
    (type: PartiposterTagType) =>
    (state: State): PartiposterTag[] => {
        const partiposterState: PartiposterState = selectPartiposter(state)
        return partiposterState.partiposterTagType &&
            partiposterState.partiposterTagType[type] &&
            partiposterState.partiposterTagType[type].partiposterTags
            ? partiposterState.partiposterTagType[type].partiposterTags.map(
                  (uuid: string) => selectPartiposter(state).partiposterTagType[type].byId[uuid],
              )
            : []
    }
export const selectPartiposterTagsUuid =
    (type: PartiposterTagType) =>
    (state: State): string[] => {
        const partiposterState: PartiposterState = selectPartiposter(state)
        return partiposterState.partiposterTagType && partiposterState.partiposterTagType[type]
            ? partiposterState.partiposterTagType[type].partiposterTags
            : []
    }
export const selectPartiposterTagsById =
    (type: PartiposterTagType) =>
    (state: State): PartiposterTagsEntityState => {
        const partiposterState: PartiposterState = selectPartiposter(state)
        return (
            partiposterState.partiposterTagType &&
            partiposterState.partiposterTagType[type] &&
            partiposterState.partiposterTagType[type].byId
        )
    }
export const selectPartiposterProfiles = (state: State): Record<string, PartiposterProfile> => state.partiposter.byId
export const selectPartiposterProfileById =
    (id: string) =>
    (state: State): PartiposterProfile =>
        state.partiposter.byId[id]
